import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import MainLayout from './components/layout/MainLayout';
import LoginPage from './pages/LoginPage';
import LandingPage from './pages/LandingPage';
import AuthCallback from './pages/AuthCallback';
import ProtectedRoute from './components/ProtectedRoute';
import WorkersPage from './pages/WorkersPage';
import DesignCuttingPage from './pages/DesignCuttingPage';
import AssignmentsPage from './pages/AssignmentsPage';
import PaymentsPage from './pages/PaymentsPage';
import { AuthProvider } from './contexts/AuthContext';
import DesignsPage from './pages/DesignsPage';
import CompaniesPage from './pages/CompaniesPage';
import UserManagement from './pages/UserManagement';
import RoleManagement from './pages/RoleManagement';
import Dashboard from './pages/Dashboard';
import ExpensesPage from './pages/ExpensesPage';
import PurchaseOrdersPage from './pages/PurchaseOrdersPage';
import PurchasePage from './pages/PurchasePage';
import InventoryPage from './pages/InventoryPage';
import { useAuth } from './contexts/AuthContext';
import { Navigate as NavigateComponent, useLocation } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import JobWorksPage from './pages/JobWorksPage';
import JobWorkDetailsPage from './pages/JobWorkDetailsPage';
import CreateJobWorkPage from './pages/CreateJobWorkPage';
import SettingsPage from './pages/SettingsPage';
import ProductionTrackerPage from './pages/ProductionTrackerPage';
import OpeningEntryForm from './pages/OpeningEntryForm';
import QcStationPage from './pages/QcStationPage';
import PressStationPage from './pages/PressStationPage';
import RepairQueuePage from './pages/RepairQueuePage';
import CompleteDesignsPage from './pages/CompleteDesignsPage';
import FinishingDashboardPage from './pages/FinishingDashboardPage';
import DesignChatPage from './pages/DesignChatPage';
import ProductionSummaryReport from './pages/reports/ProductionSummaryReport';
import WorkerPerformanceReport from './pages/reports/WorkerPerformanceReport';
import StitchingPendingReport from './pages/reports/StitchingPendingReport';
import InventoryConsumptionReport from './pages/reports/InventoryConsumptionReport';
import JobWorkAgeingReport from './pages/reports/JobWorkAgeingReport';
import DispatchDeliveryReport from './pages/reports/DispatchDeliveryReport';
import QualityControlReport from './pages/reports/QualityControlReport';
import PressProductivityReport from './pages/reports/PressProductivityReport';
import POFulfillmentReport from './pages/reports/POFulfillmentReport';
import PendingBarcodingReport from './pages/reports/PendingBarcodingReport';
import DupattaTrackerPage from './pages/DupattaTrackerPage';
import AuditLogPage from './pages/AuditLogPage';
import VendorsPage from './pages/VendorsPage';
import ProductionOrdersPage from './pages/ProductionOrdersPage';
import ProductionOrderDetailPage from './pages/ProductionOrderDetailPage';
import MaterialQcPage from './pages/MaterialQcPage';
// DesignBomPage removed — BOM is now integrated into Designs page
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from './utils/dayjs';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

// Helper function to safely access environment variables
const getEnvValue = (key: string): string => {
  if (process.env[key]) {
    return process.env[key] as string;
  }
  return '';
};

// RedirectHandler checks if user is logged in and redirects accordingly
const RedirectHandler: React.FC = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  // Show loading indicator while auth state is being determined
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  
  // If user is logged in, redirect to dashboard
  if (user) {
    console.log('User is authenticated, redirecting to dashboard');
    return <NavigateComponent to="/dashboard" replace />;
  }
  
  // If user is not logged in, show the landing page
  return <LandingPage />;
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs}>
        <AuthProvider>
          <Routes>
            {/* Root path uses RedirectHandler to decide appropriate action based on context */}
            <Route path="/" element={<RedirectHandler />} />
            
            {/* Authentication routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            
            {/* Protected routes */}
            <Route
              path="/production"
              element={
                <ProtectedRoute module="production" action="view">
                  <MainLayout>
                    <ProductionTrackerPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/production/finishing-dashboard"
              element={
                <ProtectedRoute module="production" action="view">
                  <MainLayout>
                    <FinishingDashboardPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/production/opening-entry"
              element={
                <ProtectedRoute module="opening-entry" action="view">
                  <MainLayout>
                    <OpeningEntryForm />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/production/press"
              element={
                <ProtectedRoute module="press-station" action="view">
                  <MainLayout>
                    <PressStationPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/production/qc"
              element={
                <ProtectedRoute module="qc-station" action="view">
                  <MainLayout>
                    <QcStationPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/production/repairs"
              element={
                <ProtectedRoute module="repair-queue" action="view">
                  <MainLayout>
                    <RepairQueuePage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/production/complete-designs"
              element={
                <ProtectedRoute module="production" action="view">
                  <MainLayout>
                    <CompleteDesignsPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Dashboard />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/workers"
              element={
                <ProtectedRoute module="workers" action="view">
                  <MainLayout>
                    <WorkersPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/assignments"
              element={
                <ProtectedRoute module="assignments" action="view">
                  <MainLayout>
                    <AssignmentsPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payments"
              element={
                <ProtectedRoute module="payments" action="view">
                  <MainLayout>
                    <PaymentsPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/expenses"
              element={
                <ProtectedRoute module="expenses" action="view">
                  <MainLayout>
                    <ExpensesPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/design-cutting"
              element={
                <ProtectedRoute module="design-cutting" action="view">
                  <MainLayout>
                    <DesignCuttingPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/designs"
              element={
                <ProtectedRoute module="designs" action="view">
                  <MainLayout>
                    <DesignsPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/designs/chat"
              element={
                <ProtectedRoute module="designs" action="view">
                  <MainLayout>
                    <DesignChatPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dupatta-tracker"
              element={
                <ProtectedRoute module="dupatta-tracker" action="view">
                  <MainLayout>
                    <DupattaTrackerPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/audit-log"
              element={
                <ProtectedRoute module="audit-logs" action="view">
                  <MainLayout>
                    <AuditLogPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/production-orders"
              element={
                <ProtectedRoute module="production" action="view">
                  <MainLayout>
                    <ProductionOrdersPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/production-orders/:id"
              element={
                <ProtectedRoute module="production" action="view">
                  <MainLayout>
                    <ProductionOrderDetailPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/material-qc"
              element={
                <ProtectedRoute module="qc-station" action="view">
                  <MainLayout>
                    <MaterialQcPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/companies"
              element={
                <ProtectedRoute module="companies" action="view">
                  <MainLayout>
                    <CompaniesPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/materials"
              element={
                <ProtectedRoute module="materials" action="view">
                  <MainLayout>
                    <PurchasePage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendors"
              element={
                <ProtectedRoute module="vendors" action="view">
                  <MainLayout>
                    <VendorsPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/purchase-orders"
              element={
                <ProtectedRoute module="purchase-orders" action="view">
                  <MainLayout>
                    <PurchaseOrdersPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory"
              element={
                <ProtectedRoute module="inventory" action="view">
                  <MainLayout>
                    <InventoryPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute module="users" action="view">
                  <MainLayout>
                    <UserManagement />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/roles"
              element={
                <ProtectedRoute module="users" action="view">
                  <MainLayout>
                    <RoleManagement />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/test-users"
              element={
                <div style={{padding: '20px'}}>
                  <h2>Debug Info Page</h2>
                  <p><strong>This is a diagnostic page for troubleshooting. No API calls are made.</strong></p>
                  <hr/>
                  <div style={{backgroundColor: '#f5f5f5', padding: '20px', borderRadius: '4px', marginBottom: '20px'}}>
                    <h3>Local Storage Contents:</h3>
                    <pre style={{textAlign: 'left', maxHeight: '300px', overflow: 'auto', backgroundColor: '#eeeeff', padding: '10px', borderRadius: '4px'}}>
                      {JSON.stringify({
                        token: localStorage.getItem('token') 
                          ? 'exists: ' + localStorage.getItem('token')?.substring(0, 10) + '...' 
                          : 'missing',
                        user: localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user') || '{}') : 'missing'
                      }, null, 2)}
                    </pre>
                  </div>
                  
                  <div style={{backgroundColor: '#f5f5f5', padding: '20px', borderRadius: '4px', marginBottom: '20px'}}>
                    <h3>Browser Info:</h3>
                    <pre style={{textAlign: 'left', backgroundColor: '#eeeeff', padding: '10px', borderRadius: '4px'}}>
                      {JSON.stringify({
                        userAgent: navigator.userAgent,
                        language: navigator.language,
                        cookiesEnabled: navigator.cookieEnabled,
                        currentUrl: window.location.href
                      }, null, 2)}
                    </pre>
                  </div>
                  
                  <button
                    onClick={() => {
                      console.log('Opening developer console test');
                      alert('Check if you can see messages in the developer console. A test message was just logged.');
                    }}
                    style={{
                      backgroundColor: '#2196F3',
                      color: 'white',
                      padding: '10px 20px',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      marginRight: '10px'
                    }}
                  >
                    Test Console
                  </button>
                  
                  <button
                    onClick={() => {
                      localStorage.clear();
                      alert('Local storage cleared. You will be logged out.');
                      window.location.href = '/login';
                    }}
                    style={{
                      backgroundColor: '#F44336',
                      color: 'white',
                      padding: '10px 20px',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Clear Storage & Logout
                  </button>
                </div>
              }
            />
            
            {/* API Configuration Debug Route */}
            <Route
              path="/api-debug"
              element={
                <div style={{padding: '20px'}}>
                  <h2>API Configuration Debug</h2>
                  <p><strong>This page shows the API configuration being used by the frontend.</strong></p>
                  <hr/>
                  <div style={{backgroundColor: '#f5f5f5', padding: '20px', borderRadius: '4px', marginBottom: '20px'}}>
                    <h3>Environment Variables:</h3>
                    <pre style={{textAlign: 'left', maxHeight: '300px', overflow: 'auto', backgroundColor: '#eeeeff', padding: '10px', borderRadius: '4px'}}>
                      {JSON.stringify({
                        REACT_APP_API_URL: process.env.REACT_APP_API_URL || 'Not defined',
                        apiBaseUrl: process.env.REACT_APP_API_URL || 'http://localhost:9000/api/v1',
                        mode: process.env.NODE_ENV,
                        prod: process.env.NODE_ENV === 'production',
                        dev: process.env.NODE_ENV === 'development',
                      }, null, 2)}
                    </pre>
                  </div>
                  
                  <div style={{backgroundColor: '#f5f5f5', padding: '20px', borderRadius: '4px'}}>
                    <h3>Actions:</h3>
                    <button
                      onClick={() => {
                        window.location.href = '/login';
                      }}
                      style={{
                        backgroundColor: '#2196F3',
                        color: 'white',
                        padding: '10px 20px',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginRight: '10px'
                      }}
                    >
                      Go to Login
                    </button>
                  </div>
                </div>
              }
            />
            
            {/* Job Works Route */}
            <Route
              path="/job-works"
              element={
                <ProtectedRoute module="job-works" action="view">
                  <MainLayout>
                    <JobWorksPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/job-works/new"
              element={
                <ProtectedRoute module="job-works" action="add">
                  <MainLayout>
                    <CreateJobWorkPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/job-works/:id"
              element={
                <ProtectedRoute module="job-works" action="view">
                  <MainLayout>
                    <JobWorkDetailsPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/job-works/:id/edit"
              element={
                <ProtectedRoute module="job-works" action="edit">
                  <MainLayout>
                    <CreateJobWorkPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            
            {/* Settings Route */}
            <Route
              path="/settings"
              element={
                <ProtectedRoute module="settings" action="view">
                  <MainLayout>
                    <SettingsPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            
            {/* Report Routes */}
            <Route path="/reports/production-summary" element={<ProtectedRoute module="report-production-summary" action="view"><MainLayout><ProductionSummaryReport /></MainLayout></ProtectedRoute>} />
            <Route path="/reports/worker-performance" element={<ProtectedRoute module="report-worker-performance" action="view"><MainLayout><WorkerPerformanceReport /></MainLayout></ProtectedRoute>} />
            <Route path="/reports/stitching-pending" element={<ProtectedRoute module="report-stitching-pending" action="view"><MainLayout><StitchingPendingReport /></MainLayout></ProtectedRoute>} />
            <Route path="/reports/inventory-consumption" element={<ProtectedRoute module="report-inventory-consumption" action="view"><MainLayout><InventoryConsumptionReport /></MainLayout></ProtectedRoute>} />
            <Route path="/reports/job-work-ageing" element={<ProtectedRoute module="report-job-work-ageing" action="view"><MainLayout><JobWorkAgeingReport /></MainLayout></ProtectedRoute>} />
            <Route path="/reports/dispatch-delivery" element={<ProtectedRoute module="report-dispatch-delivery" action="view"><MainLayout><DispatchDeliveryReport /></MainLayout></ProtectedRoute>} />
            <Route path="/reports/quality-control" element={<ProtectedRoute module="report-quality-control" action="view"><MainLayout><QualityControlReport /></MainLayout></ProtectedRoute>} />
            <Route path="/reports/press-productivity" element={<ProtectedRoute module="report-press-productivity" action="view"><MainLayout><PressProductivityReport /></MainLayout></ProtectedRoute>} />
            <Route path="/reports/po-fulfillment" element={<ProtectedRoute module="report-po-fulfillment" action="view"><MainLayout><POFulfillmentReport /></MainLayout></ProtectedRoute>} />
            <Route path="/reports/pending-barcoding" element={<ProtectedRoute module="report-pending-barcoding" action="view"><MainLayout><PendingBarcodingReport /></MainLayout></ProtectedRoute>} />

            {/* Catch-all route for any unmatched paths */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </AuthProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default App; 